import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCardV1 } from '../../../../features/User/UserCard';
import { useTemplate } from '../../../../features/template/TemplateProvider';
import useApi from '../../../../hook/useApi';
import { ButtonCancel, ButtonPrimary } from '../../../../utils/Button';
import { GetDate, GetTime } from '../../../../utils/Show';
import { mkRelativeUrl } from '../../../../utils/URL';
import cn from '../../../../utils/cn';

function ModalRenewService({ serviceInfoData, handleReload, closeModal }) {
    const navigate = useNavigate();
    const { setMessage } = useTemplate();
    const { put } = useApi();
    const [loading, setLoading] = useState(false);

    // Combine service info with user data
    const serviceData = {
        ...serviceInfoData?.serviceInfo,
        user: serviceInfoData?.user
    };

    const handleCreateInvoice = async () => {
        try {
            setLoading(true);
            const { error, message, data } = await put(
                `/api/purchase/service/${serviceData.id}/renew`
            );
            if (error) {
                throw new Error(message);
            }
            setMessage({ type: 'success', text: message });
            closeModal();

            // Reload the service data after successful renewal
            if (handleReload) {
                handleReload();
            }

            // Redirect to invoice info page if invoice ID is returned
            if (data?.invoiceId) {
                navigate(mkRelativeUrl(`/billing/invoices/${data.invoiceId}/info`));
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusStyles = {
            completed: 'bg-success-100 text-success-700',
            ongoing: 'bg-warning-100 text-warning-700',
            pending: 'bg-info-100 text-info-700',
            cancelled: 'bg-error-100 text-error-700',
            initiate: 'bg-info-100 text-info-700',
            implement: 'bg-warning-100 text-warning-700'
        };

        return (
            <span
                className={cn(
                    'px-2 py-1 rounded-full text-xs font-medium capitalize',
                    statusStyles[status] || 'bg-gray-100 text-gray-700'
                )}
            >
                {status}
            </span>
        );
    };

    return (
        <div className="w-full">
            {/* Info Message */}
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Renewing this service will create a new invoice for the
                    customer. Please review the service and customer information below before
                    proceeding.
                </p>
            </div>

            {/* Customer Information */}
            <div className="mb-4">
                <h3 className="text-lg font-medium text-primary mb-3">Customer Information</h3>
                <div className="bg-secondary-50 rounded-lg p-4">
                    <UserCardV1
                        title={serviceData?.user?.fullName}
                        subTitle={serviceData?.user?.organization}
                        className="mb-3"
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Email
                            </label>
                            <p className="text-sm text-primary">
                                {serviceData?.user?.primaryEmail || 'N/A'}
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Phone
                            </label>
                            <p className="text-sm text-primary">
                                {serviceData?.user?.primaryPhone || 'N/A'}
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Agency ID
                            </label>
                            <p className="text-sm text-primary">
                                #{serviceData?.user?.agencyId || 'N/A'}
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Short Code
                            </label>
                            <p className="text-sm text-primary">
                                {serviceData?.user?.shortCode || 'N/A'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Service Information */}
            <div className="mb-4">
                <h3 className="text-lg font-medium text-primary mb-3">Service Information</h3>
                <div className="bg-secondary-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Service ID
                            </label>
                            <p className="text-sm text-primary font-medium">#{serviceData.id}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Service Name
                            </label>
                            <p className="text-sm text-primary font-medium">
                                {serviceData.serviceName}
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Product Type
                            </label>
                            <p className="text-sm text-primary capitalize">
                                {serviceData.productType}
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Status
                            </label>
                            <div>{getStatusBadge(serviceData.status)}</div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Application ID
                            </label>
                            <p className="text-sm text-primary">#{serviceData.applicationId}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Subscription ID
                            </label>
                            <p className="text-sm text-primary">
                                {serviceData.subscriptionId
                                    ? `#${serviceData.subscriptionId}`
                                    : 'N/A'}
                            </p>
                        </div>
                    </div>

                    {/* Timeline Information */}
                    <div className="mt-4 pt-4 border-t border-secondary-200">
                        <h4 className="text-sm font-medium text-primary-700 mb-2">Timeline</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                            <div>
                                <span className="text-primary-700">Initiated:</span>
                                <span className="ml-2 text-primary">
                                    {serviceData.timeInitiate
                                        ? GetDate(serviceData.timeInitiate)
                                        : 'N/A'}
                                </span>
                            </div>
                            <div>
                                <span className="text-primary-700">Implemented:</span>
                                <span className="ml-2 text-primary">
                                    {serviceData.timeImplement
                                        ? GetDate(serviceData.timeImplement)
                                        : 'N/A'}
                                </span>
                            </div>
                            <div>
                                <span className="text-primary-700">Ongoing:</span>
                                <span className="ml-2 text-primary">
                                    {serviceData.timeOngoing
                                        ? GetDate(serviceData.timeOngoing)
                                        : 'N/A'}
                                </span>
                            </div>
                            <div>
                                <span className="text-primary-700">Completed:</span>
                                <span className="ml-2 text-primary">
                                    {serviceData.timeDone ? GetDate(serviceData.timeDone) : 'N/A'}
                                </span>
                            </div>
                        </div>
                        <div className="mt-2">
                            <span className="text-primary-700 text-xs">Last Updated:</span>
                            <span className="ml-2 text-primary text-xs">
                                {serviceData.timeUpdated
                                    ? `${GetDate(serviceData.timeUpdated)} ${GetTime(serviceData.timeUpdated)}`
                                    : 'N/A'}
                            </span>
                        </div>
                    </div>

                    {/* Service Period */}
                    {(serviceData.timeServiceStart || serviceData.timeServiceEnd) && (
                        <div className="mt-4 pt-4 border-t border-secondary-200">
                            <h4 className="text-sm font-medium text-primary-700 mb-2">
                                Service Period
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                <div>
                                    <span className="text-primary-700">Start Date:</span>
                                    <span className="ml-2 text-primary">
                                        {serviceData.timeServiceStart
                                            ? GetDate(serviceData.timeServiceStart)
                                            : 'N/A'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-primary-700">End Date:</span>
                                    <span className="ml-2 text-primary">
                                        {serviceData.timeServiceEnd
                                            ? GetDate(serviceData.timeServiceEnd)
                                            : 'One Time'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Package Information */}
            {serviceData.packageName && (
                <div className="mb-4">
                    <h3 className="text-lg font-medium text-primary mb-3">Package Information</h3>
                    <div className="bg-secondary-50 rounded-lg p-4">
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Package Name
                            </label>
                            <p className="text-sm text-primary">{serviceData.packageName}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Invoices Summary */}
            {serviceInfoData?.invoices && serviceInfoData.invoices.length > 0 && (
                <div className="mb-4">
                    <h3 className="text-lg font-medium text-primary mb-3">
                        Recent Invoices ({serviceInfoData.invoices.length})
                    </h3>
                    <div className="bg-secondary-50 rounded-lg p-4">
                        <div className="text-xs text-primary-700">
                            <p>
                                This service has {serviceInfoData.invoices.length} associated
                                invoice(s). Renewing will create a new invoice.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200">
                <ButtonCancel title="Cancel" onClick={() => closeModal()} disabled={loading} />
                <ButtonPrimary
                    title={loading ? 'Renewing Service...' : 'Renew Service'}
                    onClick={handleCreateInvoice}
                    loading={loading}
                    disabled={loading}
                />
            </div>
        </div>
    );
}

export default ModalRenewService;
